import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import React, { useState } from 'react';
import ResourceSelectDialog from '../Common/ResourceSelectDialog';
import Customers from '../Customers/Customer';
import { useAppDispatch } from '../../store/hooks';
import { setCustomSelectOpen } from '../../store/reducers/customSelect';
import { DateTimePicker } from '@mui/x-date-pickers';
import EventBus from '../../EventBus';
import { EventBusEventsEnum } from '../../enums/EventBusEventsEnum';
import { CustomSnackbarTypes } from '../Common/CustomSnackBar';
import moment from 'moment';
import { PromotionalBalanceService } from '../../services/PromotionalBalanceService';
import { useNavigate } from 'react-router-dom';

const promotionalBalanceService = new PromotionalBalanceService();

function CreatePromotionalBalance() {
    const [customerId, setCustomerId] = useState<any>(null);
    const [openCustomerDialog, setOpenCustomerDialog] = useState(false);
    const [expiryDate, setExpiryDate] = useState<any>(null);
    const [amount, setAmount] = useState<string>('');
    const [amountError, setAmountError] = useState<string>('');
    const [notes, setNotes] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const handleSelectCustomer = () => {
        setOpenCustomerDialog(true);
        dispatch(setCustomSelectOpen({ open: true }));
    };

    const handleCloseCustomer = (record: any) => {
        setOpenCustomerDialog(false);
        setCustomerId(record?.id ?? null);
    };

    const handleAmountChange = (val: string) => {
        setAmount(val);
        if (val !== '' && (isNaN(Number(val)) || Number(val) <= 0)) {
            setAmountError('Must be greater than 0');
        } else {
            setAmountError('');
        }
    };

    const handleSubmit = async () => {
        if (!customerId || !expiryDate || !amount) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: 'Please fill all required fields',
                type: CustomSnackbarTypes.ERROR,
            });
            return;
        }
        if (isNaN(Number(amount)) || Number(amount) <= 0) {
            setAmountError('Must be greater than 0');
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = {
                userId: customerId,
                amount,
                expiresAt: moment(expiryDate).format('YYYY-MM-DD HH:mm:ss'),
                ...(notes.trim() && { notes: notes.trim() }),
            };
            const result = await promotionalBalanceService.addWalletCash(payload);
            navigate(`/PromotionalBalance/${result?.data?.id}/show`);
        } catch {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: 'Unable to add promotional balance',
                type: CustomSnackbarTypes.ERROR,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 560, mt: 2 }}>
            {openCustomerDialog && (
                <ResourceSelectDialog
                    name="Customers"
                    list={Customers}
                    onClose={handleCloseCustomer}
                />
            )}

            <Card variant="outlined">
                <CardContent sx={{ p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} mb={3}>
                        Add Promotional Balance
                    </Typography>

                    {/* Customer */}
                    <Box mb={2.5}>
                        <Typography variant="caption" color="text.secondary" fontWeight={500} mb={0.5} display="block">
                            Customer *
                        </Typography>
                        <Button
                            variant={customerId ? 'contained' : 'outlined'}
                            color={customerId ? 'success' : 'primary'}
                            startIcon={customerId ? <CheckCircleOutlineIcon /> : <PersonSearchIcon />}
                            onClick={handleSelectCustomer}
                            sx={{ textTransform: 'none', minWidth: 220 }}
                        >
                            {customerId ? `Customer ID: ${customerId}` : 'Select Customer'}
                        </Button>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Amount */}
                    <Box mb={2.5}>
                        <TextField
                            label="Amount *"
                            value={amount}
                            onChange={(e) => handleAmountChange(e.target.value)}
                            error={!!amountError}
                            helperText={amountError}
                            size="small"
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">₹</InputAdornment>
                                ),
                            }}
                            type="number"
                            inputProps={{ min: 1 }}
                        />
                    </Box>

                    {/* Expiry Date */}
                    <Box mb={2.5}>
                        <DateTimePicker
                            label="Expiry Date *"
                            inputFormat="dd MMM yyyy HH:mm:ss"
                            value={expiryDate}
                            disableMaskedInput
                            onChange={(v) => setExpiryDate(v)}
                            renderInput={(params) => (
                                <TextField {...params} size="small" fullWidth />
                            )}
                        />
                    </Box>

                    {/* Notes */}
                    <Box mb={3}>
                        <TextField
                            label="Notes (optional)"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            multiline
                            rows={3}
                            size="small"
                            fullWidth
                            placeholder="Add a note for this balance…"
                        />
                    </Box>

                    <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        startIcon={<AccountBalanceWalletIcon />}
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {isSubmitting ? 'Adding…' : 'Add Balance'}
                    </Button>
                </CardContent>
            </Card>
        </Box>
    );
}

export default CreatePromotionalBalance;
